/**
 * Same-peer outbound cooldown after a remote decline / busy (2026-08-19
 * Zach→Olivia: callee REJECT then a new INVITE 1.3s later).
 *
 * Tombstones stop *rehydrate of the same callId*. They do not stop
 * `startCallWithMedia` minting a *new* callId ("Try again" ghost-tap, a
 * click-through on the chat Call button, or any other auto-redial). This
 * guard is that missing piece: after declined/busy, do not dial that peer
 * again until the cooldown elapses. Storage is in-memory only — a reload
 * may redial, which is an explicit user action.
 */

export const REMOTE_DECLINE_REDIAL_COOLDOWN_MS = 3_000

const BLOCK_REASONS = new Set(['declined', 'busy'])

type RemoteRejectStamp = {
  peerUserId: string
  reason: string
  atMs: number
}

let lastRemoteReject: RemoteRejectStamp | null = null

/** Record that the remote party just declined / returned busy. */
export function noteRemoteCallRejected(
  peerUserId: string,
  reason: string,
  nowMs: number = Date.now(),
): void {
  if (!peerUserId || !BLOCK_REASONS.has(reason)) return
  lastRemoteReject = { peerUserId, reason, atMs: nowMs }
}

/** True when an outbound start to this peer would auto-redial a just-declined call. */
export function isOutboundRedialBlocked(
  peerUserId: string,
  nowMs: number = Date.now(),
): boolean {
  if (!peerUserId || !lastRemoteReject) return false
  if (lastRemoteReject.peerUserId !== peerUserId) return false
  return nowMs - lastRemoteReject.atMs < REMOTE_DECLINE_REDIAL_COOLDOWN_MS
}

/** Remaining ms before the ended-call "Try again" control should accept a tap. */
export function remainingRecallEnableMs(
  endedAtMs: number | undefined,
  nowMs: number = Date.now(),
): number {
  if (!endedAtMs || !Number.isFinite(endedAtMs)) return REMOTE_DECLINE_REDIAL_COOLDOWN_MS
  return Math.max(0, endedAtMs + REMOTE_DECLINE_REDIAL_COOLDOWN_MS - nowMs)
}

/** Test hook. */
export function clearOutboundRedialGuard(): void {
  lastRemoteReject = null
}
